<template>
    <div class="pin-drop-result" :class="{'is-loading' : pinDrop.loading}">
        <div class="pin-drop-result__loader" v-if="pinDrop.loading">
            <Loader />
        </div>
        <div v-else>
            <div class="pin-drop-result__top">
                <div class="pin-drop-result__title h1">{{ !pinDrop.data.comment && pinDrop.data.label ? pinDrop.data.label : pinDrop.data.name }}</div>
                <div class="pin-drop-result__actions">
                    <div class="pin-drop-result__actions__item" v-if="showShare">
                        <ShareButton />
                    </div>
                    <div class="pin-drop-result__actions__item">
                        <FavouriteButton :favouriteObject="pinDrop.data" :trackingData="{ category: 'Route Planner', action: 'Pindrop', label: 'Save' }" />
                    </div>
                    <div class="pin-drop-result__actions__item" @click="closePinDropClickHandler">
                        <Icon>
                            <IconClose />
                        </Icon>
                    </div>
                </div>
            </div>
            <div class="pin-drop-result__text copy-light">
                <span v-if="pinDrop.data.realLocation && pinDrop.data.comment">{{ pinDrop.data.comment }}</span>
                <span v-else>Koordinaten <br> {{ pinDrop.data.lat }}, {{ pinDrop.data.lon }}</span>
            </div>
            <div class="pin-drop-result__buttons">
                <template v-for="(action, index) in availableActions">
                    <SmrButton v-if="showButton(action.action)" :key="index"  size="tiny" :round="true" color="grey" :outline="true" :icon="true" @click.native="buttonActionClickHandler(action.action, action.label)">
                        <Icon slot="icon">
                            <component :is="action.icon"></component>
                        </Icon>
                        {{ action.label }}
                    </SmrButton>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import eventBus from '@/utils/event-bus.js';
import ShareButton from '@/components/ShareButton.vue'
import FavouriteButton from '@/components/FavouriteButton.vue'
import Loader from '@/components/Loader.vue'
import Icon from '@/components/Icon.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconEdit from '@/components/icons/IconEdit.vue'
import IconPlusCircle from '@/components/icons/IconPlusCircle.vue'
import IconMinusCircle from '@/components/icons/IconMinusCircle.vue'
import IconSearchMarkStart from '@/components/icons/IconSearchMarkStart.vue'
import IconSearchMarkTarget from '@/components/icons/IconSearchMarkTarget.vue'
import SmrButton from '@/components/SmrButton.vue'
import { trackEvent } from '@/utils/tracking'

export default {
    components: {
        ShareButton,
        FavouriteButton,
        Loader,
        SmrButton,
        Icon,
        IconClose,
        IconEdit,
        IconPlusCircle,
        IconMinusCircle,
        IconSearchMarkStart,
        IconSearchMarkTarget,
    },
    data() {
        return {
            showShare: false,
            defaultActions: [
                {
                    label: 'Als Start',
                    action: 'start',
                    icon: 'IconSearchMarkStart'
                },
                {
                    label: 'Als Zwischenziel',
                    action: 'between',
                    icon: 'IconPlusCircle'
                },
                {
                    label: 'Als Ziel',
                    action: 'end',
                    icon: 'IconSearchMarkTarget'
                },
            ],
            routeMarkerActions: [
                {
                    label: 'Bearbeiten',
                    action: 'edit',
                    icon: 'IconEdit'
                },
                {
                    label: 'Entfernen',
                    action: 'remove',
                    icon: 'IconMinusCircle'
                },
            ]
        }
    },
    computed: {
        ...mapGetters(['pinDrop', 'routeWaypoints', 'waypointPositions', 'interimWaypoints', 'activeTransportModeNoIntermediateStops']),
        type() {
            if(this.pinDrop) return this.pinDrop.data.type
            return false
        },
        availableActions() {
            if(this.type === 'routemarker') {
                return this.routeWaypoints.length > 2 ? this.routeMarkerActions : [...this.routeMarkerActions].filter(a => a.action !== 'remove')
            } else if(this.activeTransportModeNoIntermediateStops) {
                return this.defaultActions.filter(a => a.action !== 'between')
            } else {
                return this.defaultActions
            }
        }
    },
    methods: {
        closePinDropClickHandler() {
            this.removePinDrop()
            trackEvent({ category: 'Route Planner', action: 'Pindrop', label: 'Close' })
        },
        buttonActionClickHandler(action, label) {
            switch(action) {
                case 'start':
                case 'between':
                case 'end':
                    this.addToWaypointsHandler(action, label)
                    break;
                case 'edit':
                case 'remove': {
                    const waypointIndex = this.pinDrop.data.waypointIndex
                    this.removePinDrop()
                    eventBus.$emit('editWaypointsWithAction', { action: action, waypointIndex: waypointIndex })
                    break;
                }
            }
        },
        removePinDrop() {
            eventBus.$emit('removePinDrop', { type:  this.type })
            this.$store.commit('setPinDropData', false)
        },
        addToWaypointsHandler(position, trackingLabel) {
            const data = this.pinDrop.data
            this.removePinDrop()
            eventBus.$emit('addLocationToWaypoints', { 
                waypoint: data,
                position: position,
                positionIndex: false,
                insertAfterIndex: false,
                disableLocationJump: true,
            })

            if(trackingLabel) {
                trackEvent({ category: 'Route Planner', action: 'Pindrop', label: trackingLabel })
            }
        },
        showButton(position) {
            if(position === 'between') {
                if((this.waypointPositions.start && this.interimWaypoints.length <= 99) || (this.waypointPositions.end && this.interimWaypoints.length <= 99)) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        }
    }
}
</script>

<style lang="scss">
.pin-drop-result {
    padding-bottom: 24px;
    min-height: 190px;

    &.is-loading {
        display: flex;
    }

    &__loader {
        padding: 24px 0 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
    }
    
    &__top {
        padding: 18px 0;
        display: flex;
        justify-content: space-between;
    }
    &__actions {
        display: flex;
        color: $color-grey-dark;
        padding-left: 20px;

        &__item {
            margin: 0 6px;
            cursor: pointer;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    &__buttons {
        margin-top: 24px;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
}
</style>